import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
    return String(detail);
};

export default function AdminLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
            navigate("/admin");
        } catch (err) {
            setError(formatError(err.response?.data?.detail));
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-[#081226] flex items-center justify-center px-6">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0E1B33] p-8 shadow-[0_0_60px_rgba(201,169,98,0.08)]"
            >
                <div className="flex items-center justify-center gap-2.5 mb-6">
                    <img src="/assets/logo_mark.png" alt="Ink of Destiny" className="h-12 w-auto" />
                    <span className="font-display italic text-xl text-[#F0EBE0]">Ink of <span className="text-[#C9A962]">Destiny</span></span>
                </div>
                <h1 className="font-display text-2xl font-semibold text-[#F0EBE0] text-center">Admin Login</h1>
                <p className="mt-1 text-xs text-[#5C5A56] text-center uppercase tracking-[0.2em]">Leads Dashboard</p>

                <form data-testid="admin-login-form" onSubmit={submit} className="mt-7 space-y-4">
                    <input
                        data-testid="admin-email-input"
                        type="email"
                        required
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="gold-ring-focus w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-3.5 text-sm text-[#F0EBE0] placeholder-[#5C5A56]"
                    />
                    <input
                        data-testid="admin-password-input"
                        type="password"
                        required
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="gold-ring-focus w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-3.5 text-sm text-[#F0EBE0] placeholder-[#5C5A56]"
                    />
                    {error && (
                        <p data-testid="admin-login-error" className="text-xs text-red-400 text-center">{error}</p>
                    )}
                    <button
                        data-testid="admin-login-submit"
                        type="submit"
                        disabled={loading}
                        className="w-full py-3.5 rounded-full bg-[#C9A962] text-[#081226] text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#dcc083] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        <Lock size={15} /> {loading ? "Signing in…" : "Sign In"}
                    </button>
                </form>
                <a href="/" data-testid="admin-back-home" className="block mt-6 text-center text-xs text-[#5C5A56] hover:text-[#C9A962] transition-colors">
                    ← Back to website
                </a>
            </motion.div>
        </div>
    );
}
